/**
 * The stable API error catalogue (docs/API.md §2).
 *
 * This is the wire vocabulary. The API, the worker (for job failures surfaced
 * back through the outbox/notification path) and the SPA (for error-specific
 * UI, e.g. offering a reload on VERSION_CONFLICT) all import this rather than
 * inventing string literals at the call site.
 *
 * DELIBERATE DECISION — no `TICKET_NOT_VISIBLE` wire code:
 * docs/SECURITY.md §1 requires GET /tickets/:id to return an identical 404
 * whether the ticket does not exist or exists but is outside the caller's
 * visibility scope, because ticket numbers are sequential and enumerable —
 * a distinguishable 403 would let anyone sweep the number space and infer
 * total ticket volume and creation velocity. `NOT_FOUND` covers both cases
 * on the wire. The visible/absent distinction may still be recorded as an
 * internal log/audit reason (e.g. for the deployment's own debugging), but
 * it must never be serialised into a response body, a header, or any other
 * client-observable signal.
 */
import { z } from 'zod';
const ERROR_CODE_LIST = [
    'VALIDATION_ERROR',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'PRECONDITION_REQUIRED',
    'VERSION_CONFLICT',
    'INVALID_TRANSITION',
    'STALE_RESOLUTION',
    'IDEMPOTENCY_KEY_REUSED',
    'OUTSIDE_REOPEN_WINDOW',
    'INVALID_CONFIRMATION',
    'RATE_LIMITED',
    'DEPENDENCY_ERROR',
    'INTERNAL_ERROR',
];
export const ERROR_CODES = {
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    PRECONDITION_REQUIRED: 'PRECONDITION_REQUIRED',
    VERSION_CONFLICT: 'VERSION_CONFLICT',
    INVALID_TRANSITION: 'INVALID_TRANSITION',
    STALE_RESOLUTION: 'STALE_RESOLUTION',
    IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
    OUTSIDE_REOPEN_WINDOW: 'OUTSIDE_REOPEN_WINDOW',
    INVALID_CONFIRMATION: 'INVALID_CONFIRMATION',
    RATE_LIMITED: 'RATE_LIMITED',
    DEPENDENCY_ERROR: 'DEPENDENCY_ERROR',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
};
/**
 * HTTP status mapping, binding per docs/API.md §2. `428` vs `412` vs `409`
 * are deliberately distinct statuses, not just distinct codes at the same
 * status: `428` means the client didn't send `If-Match` at all, `412` means
 * it sent a stale one, and `409` means the request was well-formed and
 * current but conflicts with a domain rule.
 */
export const ERROR_STATUS = {
    VALIDATION_ERROR: 400,
    UNAUTHENTICATED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    PRECONDITION_REQUIRED: 428,
    VERSION_CONFLICT: 412,
    INVALID_TRANSITION: 409,
    STALE_RESOLUTION: 409,
    IDEMPOTENCY_KEY_REUSED: 409,
    OUTSIDE_REOPEN_WINDOW: 422,
    INVALID_CONFIRMATION: 422,
    RATE_LIMITED: 429,
    DEPENDENCY_ERROR: 503,
    INTERNAL_ERROR: 500,
};
const ERROR_CODE_VALUES = Object.values(ERROR_CODES);
export const errorCodeSchema = z.enum(ERROR_CODE_VALUES);
export const errorDetailSchema = z.object({
    field: z.string(),
    issue: z.string(),
});
/**
 * The response envelope, exactly as shown in docs/API.md §2. `details` is
 * only present for `VALIDATION_ERROR`-shaped field-level feedback; every
 * other error code omits it.
 *
 * Never populate `message` from a caught exception's own `.message` for an
 * unclassified failure — that is how stack-trace-adjacent internals leak to
 * a client. See docs/SECURITY.md §11.
 */
export const errorEnvelopeSchema = z.object({
    error: z.object({
        code: errorCodeSchema,
        message: z.string(),
        requestId: z.string(),
        details: z.array(errorDetailSchema).optional(),
    }),
});
//# sourceMappingURL=errors.js.map